import { BackBar, BackButton } from '../../layout/back-button';
import { Link } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { LogoMark } from '../../logo/logo-mark';
import { RightArrow } from '../../icons/right-arrow';
import { useAuth } from '../../../context/auth-provider';
import { publicApi } from '../../../api/public-client';
import {
  AutoHeight,
  Collapse,
  Shake,
  StateSwitch,
  StatusLabel,
} from '../../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  Field,
  Label,
  PasswordWrap,
  PasswordInput,
  PasswordToggle,
  FullWidthButton,
  FieldError,
  HelperText,
  InfoBanner,
  TopLink,
  HeadingSkeleton,
  FieldSkeleton,
  SkeletonButton,
  SkeletonGroup,
} from '../onboarding.styled';

type LoadState = 'loading' | 'ready' | 'expired' | 'invalid';

// "I forgot my password", step two: redeem the emailed link.
//
// A HANDOFF page, like invite-accept. The person holding this link is not
// necessarily whoever this browser is signed in as, so the router does not bounce
// an authenticated visitor away from it (HANDOFF_PREFIXES in post-auth-route.ts)
// and this page ends the incumbent session itself before the redeem mints a new
// one. Without both halves, anyone already signed in on the machine would land on
// their own dashboard and the new password could never be set — the exact bug
// that made every invitation unusable.
export const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { endSession, user, authStatus } = useAuth();

  const [state, setState] = useState<LoadState>('loading');
  const [emailMasked, setEmailMasked] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [displaced, setDisplaced] = useState('');
  const ran = useRef(false);
  const handedOff = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState('invalid');

      return;
    }
    publicApi
      .get(`/password/reset/${token}`)
      .then(res => {
        setEmailMasked(res.data?.email_masked || '');
        setState('ready');
      })
      .catch(err => {
        setState(err?.response?.status === 410 ? 'expired' : 'invalid');
      });
  }, [token]);

  // Deliberately AFTER the link is known good: a dead link must not sign anybody
  // out. Once it is live, the incumbent session is revoked server-side so the two
  // identities never overlap.
  useEffect(() => {
    if (state !== 'ready' || authStatus !== 'authed' || !user) return;
    if (handedOff.current) return;
    handedOff.current = true;
    setDisplaced(user.full_name || user.email || 'the previous user');
    endSession();
  }, [state, authStatus, user, endSession]);

  const refuse = (message: string) => {
    setError(message);
    setAttempt(n => n + 1);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      refuse('Password must be at least 8 characters');

      return;
    }
    if (password !== confirm) {
      refuse('Passwords do not match');

      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await publicApi.post('/password/reset', { token, password });
      // The redeem revoked every session for this account and issued a fresh one
      // in the cookies of THIS browser. A full reload is the honest way to adopt
      // it: AuthProvider re-hydrates from /auth, which is the single source of
      // truth for who is signed in, rather than this page guessing.
      window.location.assign('/');
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      const status = ax.response?.status;
      if (status === 410) setState('expired');
      else if (status === 404) setState('invalid');
      else
        refuse(ax.response?.data?.message || 'Could not reset your password.');
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <Card>
        {/* TOP LEFT, the product's one back control. These cards carried a
            <TopLink> anchor styled as body copy at the FOOT of the card — the
            name said top, the position said bottom, and on the reset form it
            sat under a submit button so the way out was below the fold. */}
        <BackBar>
          <BackButton to="/" label="Back to sign in" />
        </BackBar>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>

        {/* Four fills of one card. The placeholder is the shape of the form
            that is about to land, so the card is already the right height
            while the link is checked; expired / invalid / ready then crossfade
            and the card tweens to whatever height the fill needs. */}
        <AutoHeight>
          <StateSwitch state={state}>
            {state === 'loading' && (
              <SkeletonGroup label="Checking your reset link">
                <HeadingSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <SkeletonButton aria-hidden="true" />
              </SkeletonGroup>
            )}

            {(state === 'expired' || state === 'invalid') && (
              <>
                <Heading>
                  {state === 'expired'
                    ? 'This reset link has expired'
                    : 'This reset link isn’t valid'}
                </Heading>
                <Subheading>
                  {state === 'expired'
                    ? 'Reset links work once and only for a short while. Ask for a fresh one.'
                    : 'It may have already been used. Request a new link to continue.'}
                </Subheading>
                <TopLink>
                  <Link to="/forgot-password">Request a new link</Link>
                </TopLink>
              </>
            )}

            {state === 'ready' && (
              <>
                <Heading>Choose a new password</Heading>
                <Subheading>
                  Setting it signs you out everywhere else, so any device that
                  had the old password stops working.
                </Subheading>
                <Collapse open={!!emailMasked}>
                  <InfoBanner>Resetting {emailMasked}</InfoBanner>
                </Collapse>
                {/* Arrives AFTER the form, when endSession resolves — so it
                    unfolds rather than shoving the fields down. */}
                <Collapse open={!!displaced}>
                  <HelperText>
                    {displaced} was signed out on this device so this link could
                    be used.
                  </HelperText>
                </Collapse>

                <form onSubmit={submit}>
                  <Field>
                    <Label htmlFor="rp-pass">New password</Label>
                    <Shake active={!!error} attempt={attempt}>
                      <PasswordWrap>
                        <PasswordInput
                          id="rp-pass"
                          name="password"
                          type={show ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          hasError={!!error}
                          placeholder="At least 8 characters"
                          autoComplete="new-password"
                          aria-describedby="password-rule"
                        />
                        <PasswordToggle
                          type="button"
                          onClick={() => setShow(s => !s)}
                        >
                          <StatusLabel
                            state={show ? 'hide' : 'show'}
                            labels={{ show: 'Show', hide: 'Hide' }}
                          />
                        </PasswordToggle>
                      </PasswordWrap>
                    </Shake>
                    {/* THE RULE, BEFORE THE ATTEMPT.
                        The only password requirement this product enforces is
                        length, and it was communicated ONLY as a placeholder —
                        which vanishes the moment the field is typed in, exactly
                        when it is needed, and which a screen reader announces
                        once and never again. A rule the user is told about
                        after being refused is a rule they meet by trial and
                        error. */}
                    <HelperText id="password-rule">
                      At least 8 characters. Spaces are allowed and count — a
                      short sentence makes a strong password.
                    </HelperText>
                  </Field>
                  <Field>
                    <Label htmlFor="rp-confirm">Confirm password</Label>
                    <Shake active={!!error} attempt={attempt}>
                      <PasswordInput
                        id="rp-confirm"
                        name="confirm_password"
                        type={show ? 'text' : 'password'}
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                        hasError={!!error}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                      />
                    </Shake>
                  </Field>
                  <FieldError message={error || undefined} />
                  <FullWidthButton type="submit" disabled={submitting}>
                    <StatusLabel
                      state={submitting ? 'busy' : 'idle'}
                      labels={{ idle: 'Set new password', busy: 'Saving…' }}
                    />{' '}
                    <RightArrow />
                  </FullWidthButton>
                </form>
              </>
            )}
          </StateSwitch>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
